/**
 * Tipos del sistema económico — Exchange (mercado de fungibles, modelo OSRS GE).
 *
 * El Exchange opera sobre items fungibles (stackeables: materiales/consumibles/
 * monedas — los que `isTemplateStackable` deja apilar). Cada `itemId` tiene un
 * libro de órdenes global con matching automático price-time (FIFO).
 *
 * Los tipos del Marketplace (ítems rolled) llegan en Fase 2. Ver
 * docs/ECONOMY_EXCHANGE_MARKETPLACE.md.
 */

/** Lado de una orden del libro. */
export enum MarketSide {
  BUY = 'buy',
  SELL = 'sell',
}

/** Razón de un depósito en el Cajón de Retiro (para feedback en la UI). */
export type MarketClaimReason = 'buy_fill' | 'sell_proceeds' | 'cancel_refund';

/**
 * Una entry del catálogo de fungibles que el Exchange ofrece para navegar.
 * El server la arma con el resumen de mercado de cada item (último precio +
 * mejores puntas) para el listado lateral.
 */
export interface ExchangeItemEntry {
  itemId: string;
  itemName: string;
  itemIcon: string;
  /** Último precio negociado (oro/unidad), o null si nunca se tradeó. */
  lastPrice: number | null;
  /** Mejor oferta de compra (bid) del libro, o null si no hay. */
  bestBid: number | null;
  /** Mejor oferta de venta (ask) del libro, o null si no hay. */
  bestAsk: number | null;
}

/** Un nivel agregado del libro: cuántas unidades hay a un precio dado. */
export interface OrderBookLevel {
  price: number;
  quantity: number;
}

/** Una orden propia del player tal como se muestra en "Mis órdenes". */
export interface MarketOrderView {
  id: string;
  itemId: string;
  side: MarketSide;
  price: number;
  qtyTotal: number;
  qtyFilled: number;
}

/** Un trade cerrado reciente (para el historial/gráfico del item). */
export interface MarketTradeView {
  price: number;
  qty: number;
  /** epoch ms. */
  closedAt: number;
}

/** Una vela OHLC del histórico de precios de un item (para el gráfico). */
export interface PriceCandle {
  /** Inicio del bucket, epoch ms. */
  ts: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

/** Un depósito pendiente en el Cajón de Retiro del player. */
export interface MarketClaimView {
  id: string;
  kind: 'item' | 'gold';
  itemId: string | null;
  itemName: string | null;
  itemIcon: string | null;
  quantity: number;
  goldAmount: number;
  reason: MarketClaimReason | null;
}

// ===========================================================================
// MARKETPLACE (ítems rolled, modelo PoE Trade)
// ===========================================================================

/** Componente de un afijo rolled (mismo shape que en el inventario). */
export type RolledAffixWire = Record<string, Array<{ v: number; t: number }>>;

/** Filtro de afijo: pide un valor mínimo para un modifierKey dado. */
export interface AffixFilter {
  modifierKey: string;
  min: number;
}

/** Ordenamiento del Marketplace. */
export type MarketplaceSort = 'price_asc' | 'price_desc' | 'score_desc' | 'recent';

/** Filtros de búsqueda avanzada del Marketplace (todos opcionales). */
export interface MarketplaceFilters {
  /** Restringe a un template puntual. */
  itemId?: string;
  /** WeaponSubtype/ArmorSubtype como string. */
  subtype?: string;
  /** Slot de equipamiento. */
  slot?: string;
  /** Rarezas incluidas (ItemRarity como number). Vacío/undefined = todas. */
  rarities?: number[];
  /** Mínimos por afijo (AND entre ellos). */
  affixMins?: AffixFilter[];
  priceMin?: number;
  priceMax?: number;
  sort?: MarketplaceSort;
  /** Paginación. */
  offset?: number;
}

/** Un listado individual del Marketplace, tal como se muestra. */
export interface MarketListingView {
  id: string;
  itemId: string;
  itemName: string;
  itemIcon: string;
  slot: string | null;
  subtype: string | null;
  enchantId: string | null;
  rolledRarity: number;
  rolledAffixes: RolledAffixWire;
  score: number;
  price: number;
  /** True si el listado es del player que pregunta (muestra "Cancelar"). */
  isMine: boolean;
  /**
   * Fracción de descuento vs el valor de mercado (mediana de comparables), 0..1.
   * Sólo presente en resultados del Deal Finder; undefined en búsquedas normales.
   */
  dealDiscount?: number;
}

/** Una venta comparable cerrada (para la tasación). */
export interface AppraisalComparable {
  price: number;
  closedAt: number;
}

/** Resultado de la tasación automática de una instancia. */
export interface AppraisalResult {
  suggestedMin: number | null;
  suggestedMax: number | null;
  median: number | null;
  sampleCount: number;
  score: number;
  comparables: AppraisalComparable[];
}

/** Opción de afijo para el panel de filtros (derivada de AFFIX_POOL). */
export interface AffixOption {
  modifierKey: string;
  displayName: string;
  displayFormat: 'flat' | 'percent';
}

// ===========================================================================
// PERFIL DE COMERCIANTE (búsquedas guardadas, watchlist, alertas)
// ===========================================================================

/** Una búsqueda guardada del Marketplace (opcionalmente alerta). */
export interface SavedSearchView {
  id: string;
  name: string;
  filters: MarketplaceFilters;
  /** Si true, avisa cuando aparece un listado nuevo que matchea. */
  alert: boolean;
}

/** Dirección de cruce de una vigilancia de precio. */
export type WatchDirection = 'below' | 'above';

/** Una vigilancia de precio del Exchange (avisa al cruzar el target). */
export interface MarketWatchView {
  id: string;
  itemId: string;
  itemName: string;
  direction: WatchDirection;
  targetPrice: number;
}

/** Una alerta disparada (saved-search match o price watch). */
export interface MarketAlertView {
  id: string;
  kind: 'listing' | 'price';
  message: string;
  /** epoch ms. */
  createdAt: number;
  seen: boolean;
}

// ===========================================================================
// CARTERA DEL COMERCIANTE (historial + P&L + estadísticas)
// ===========================================================================

/** Un trade cerrado del player, desde su perspectiva (compra o venta). */
export interface TraderTradeView {
  itemId: string;
  itemName: string;
  itemIcon: string;
  side: 'buy' | 'sell';
  price: number;
  qty: number;
  /** Fee pagado en esta operación (sólo el vendedor paga; 0 para el comprador). */
  fee: number;
  /** epoch ms. */
  closedAt: number;
}

/** Agregado de actividad del player por ítem (para detectar flips rentables). */
export interface TraderItemStat {
  itemId: string;
  itemName: string;
  boughtQty: number;
  boughtGold: number;
  soldQty: number;
  /** Oro recibido por ventas, neto de fees. */
  soldGoldNet: number;
  /** Flujo neto en este ítem: vendido neto − comprado. */
  netFlow: number;
}

/** Resumen de la cartera del comerciante (sobre las últimas N operaciones). */
export interface TraderStats {
  tradeCount: number;
  /** Oro gastado en compras. */
  goldSpent: number;
  /** Oro ganado en ventas, neto de fees. */
  goldEarned: number;
  feesPaid: number;
  /** Flujo neto = ganado − gastado. */
  netFlow: number;
  /** Oro total transaccionado (volumen). */
  volumeGold: number;
  perItem: TraderItemStat[];
  recent: TraderTradeView[];
}
